<template>
  <div
    class="sa-page"
    :class="{ 'sa-page--mobile': isMobile, 'sa-page--dark': $vuetify.theme.dark }"
  >
    <div class="sa-toolbar">
      <div class="sa-toolbar__title">
        <span class="sa-icon-tile">
          <v-icon color="primary">mdi-account-check-outline</v-icon>
        </span>
        <span class="sa-toolbar__text">{{ $t($route.name, { name: $store.state.studentLabel }) }}</span>
      </div>
      <button
        type="button"
        class="sa-back"
        :title="$t('back')"
        @click="$router.go(-1)"
      >
        <v-icon>mdi-arrow-left</v-icon>
      </button>
    </div>

    <div class="sa-body">
      <div class="sa-card sa-card--session">
        <div class="sa-card__header">
          <span class="sa-icon-tile">
            <v-icon color="primary">mdi-calendar-clock-outline</v-icon>
          </span>
          <div class="sa-card__heading">
            <div class="sa-card__title">
              {{ $t('academySession') }}
            </div>
            <div class="sa-card__subtitle">
              {{ $t('attendance-session-subtitle') }}
            </div>
          </div>
        </div>
        <div class="sa-card__body">
          <div
            v-show="!sessionProp"
            class="sa-session-picker"
          >
            <autocomplete
              v-model="session"
              title="academySession"
              type="academySession"
              :value="session"
            />
          </div>
          <div
            v-if="session"
            class="sa-meta"
          >
            <span class="sa-meta__item">
              <v-icon small>mdi-calendar</v-icon>
              {{ $t('heldingDate') }}
              <b class="sa-ltr">{{ session.jdate }}</b>
            </span>
            <span
              v-if="session.event"
              class="sa-meta__item"
            >
              <v-icon small>mdi-clock-outline</v-icon>
              {{ $t('startTime') }}
              <b class="sa-ltr">{{ session.event.start_time }}</b>
            </span>
            <span
              v-if="session.event"
              class="sa-meta__item"
            >
              <v-icon small>mdi-calendar-start</v-icon>
              {{ $t('eventStartDate') }}
              <b class="sa-ltr">{{ session.event.start_date }}</b>
            </span>
            <span
              v-if="session.event"
              class="sa-meta__item"
            >
              <v-icon small>mdi-counter</v-icon>
              {{ $t('sessionCount') }}
              <b>{{ session.event.session_count }}</b>
            </span>
            <span
              v-if="session.event && session.event.teacher && session.teacher"
              class="sa-meta__item"
            >
              <v-icon small>mdi-account-tie-outline</v-icon>
              {{ $t('teacher') }}
              <b>{{ session.teacher.name }}</b>
            </span>
            <span
              v-if="session.event && session.event.place && session.place"
              class="sa-meta__item"
            >
              <v-icon small>mdi-office-building-outline</v-icon>
              {{ $t('place') }} {{ $t('class') }}
              <b>{{ session.place.name }}</b>
            </span>
            <span
              v-if="session.event && session.event.arrayDays && session.event.arrayDays.length > 0"
              class="sa-meta__item"
            >
              <v-icon small>mdi-calendar-week</v-icon>
              {{ $t('daysHolding') }}
              <span
                v-for="(day, index) in session.event.arrayDays"
                :key="index"
                class="sa-day"
              >{{ $t(day) }}</span>
            </span>
          </div>
        </div>
      </div>

      <div
        v-if="students && students.length > 0"
        class="sa-kpis"
      >
        <div class="sa-kpi sa-kpi--total">
          <div class="sa-kpi__label">
            {{ $t('attendance-total') }}
          </div>
          <div class="sa-kpi__value">
            {{ students.length }}
          </div>
        </div>
        <div class="sa-kpi sa-kpi--present">
          <div class="sa-kpi__label">
            {{ $t('present') }}
          </div>
          <div class="sa-kpi__value">
            {{ presentCount }}
          </div>
        </div>
        <div class="sa-kpi sa-kpi--absent">
          <div class="sa-kpi__label">
            {{ $t('absent') }}
          </div>
          <div class="sa-kpi__value">
            {{ students.length - presentCount }}
          </div>
        </div>
      </div>

      <div class="sa-card sa-card--list">
        <div class="sa-card__header">
          <span class="sa-icon-tile">
            <v-icon color="primary">mdi-account-group-outline</v-icon>
          </span>
          <div class="sa-card__heading">
            <div class="sa-card__title">
              {{ $t('eventStudent', { name: $store.state.studentLabel }) }}
            </div>
            <div class="sa-card__subtitle">
              {{ $t('attendance-list-subtitle') }}
            </div>
          </div>
        </div>
        <div class="sa-list">
          <v-skeleton-loader
            v-if="loading && (!students || students.length == 0)"
            type="list-item-avatar@6"
          />
          <div
            v-else-if="session && (!students || students.length == 0)"
            class="sa-empty"
          >
            <v-icon large>
              mdi-account-off-outline
            </v-icon>
            <span>{{ $t('attendance-empty') }}</span>
          </div>
          <div
            v-for="(student, i) in students"
            :key="i"
            class="sa-row"
            :class="{ 'sa-row--present': student.attendance == 'present' }"
          >
            <span class="sa-row__index">{{ +i + +1 }}</span>
            <div class="sa-row__main">
              <div class="sa-row__name">
                {{ student.name }}
              </div>
              <div
                v-if="student.attendanceStatus"
                class="sa-row__stats"
              >
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <span
                      class="sa-stat"
                      v-bind="attrs"
                      v-on="on"
                    >
                      <v-icon x-small>mdi-calendar-check</v-icon>
                      {{ student.attendanceStatus.total }}
                    </span>
                  </template>
                  <span>{{ $t('sessionTotalHelding') }}</span>
                </v-tooltip>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <span
                      class="sa-stat sa-stat--present"
                      v-bind="attrs"
                      v-on="on"
                    >
                      <v-icon x-small>mdi-check</v-icon>
                      {{ student.attendanceStatus.present }}
                    </span>
                  </template>
                  <span>{{ $t('countPresent') }}</span>
                </v-tooltip>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <span
                      class="sa-stat sa-stat--absent"
                      v-bind="attrs"
                      v-on="on"
                    >
                      <v-icon x-small>mdi-close</v-icon>
                      {{ student.attendanceStatus.absent }}
                    </span>
                  </template>
                  <span>{{ $t('countAbsent') }}</span>
                </v-tooltip>
              </div>
            </div>
            <span class="sa-row__state">
              {{ student.attendance == 'present' ? $t('present') : $t('absent') }}
            </span>
            <v-switch
              v-model="student.attendance"
              class="sa-row__switch"
              inset
              dense
              hide-details
              color="success"
              value="present"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="sa-footer">
      <v-btn
        color="primary"
        depressed
        class="sa-footer__btn"
        :disabled="!students || students.length == 0"
        :loading="loading"
        @click="submit"
      >
        <v-icon
          left
          small
        >
          mdi-check
        </v-icon>
        {{ $t('submit') }}
      </v-btn>
    </div>

    <v-snackbar
      v-model="snakbar"
      color="(snakbarColor ? snakbarColor : '')"
      top
      style="z-index: 1000000000000 !important;"
    >
      {{ snakbarText }}

      <template v-slot:action="{ attrs }">
        <v-btn
          color="white"
          text
          v-bind="attrs"
          @click="snakbar = !snakbar"
        >
          {{ $t('close') }}
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
  import General from '../../../../Mixins/general'
  export default {
    name: 'StudentList',
    components: {
      autocomplete: () => import('../../components/general/patial/autocomplete'),
    },
    mixins: [General],
    props: ['sessionProp'],
    data: () => ({
      selected: [],
      eventStudentReq: true,
      session: null,
      excelItems: null,
      category: null,
      integratorType: null,
      rangeDate: null,
      registerar: null,
      attractionMethod: null,
      customerStatusItems: null,
      financialStatusItems: null,
      printDialog: false,
      uploadDialog: false,
      accountDialog: false,
      deletDialog: false,
      filter: false,
      financialStatus: null,
      customerStatus: null,
      status: 'active',
      taskindex: 0,
      lead: null,
      refId: null,
      taskItem: null,
      model: null,
      from: 1,
      taskDialog: null,
      taskDialogList: null,
      showSelect: null,
      tag: null,
      temp: null,
      sellStatus: null,
      items: [],
      sortBy: [],
      dialog: null,
      timer: 0,
      totalItems: 0,
      snakbarColor: null,
      snakbar: false,
      snakbarText: null,
      loading: false,
      options: {
        itemsPerPage: 5,
      },
      deleteItem: null,
      students: [],
      headers: null,
      personnel: null,
      id: null,
      search: null,
      renderTable: null,
      selectedHeaders: [],
    }),
    computed: {
      presentCount () {
        return (this.students || []).filter(s => s.attendance === 'present').length
      },
      isMobile () {
        return this.$vuetify.breakpoint.name === 'xs'
      },
      showHeaders () {
        var app = this
        return this.headers.filter(function (s) {
          if (app.type !== 'sell') {
            if (s.value === 'pre_invoice') {
            } else {
              return app.selectedHeaders.includes(s)
            }
          } else {
            return app.selectedHeaders.includes(s)
          }
        })
      },
      totals: function () {
        var app = this
        app.excelItems = []
        const totals = this.items.reduce((acc, d, index) => {
          acc.amount += parseFloat((d.remain ? d.remain.remain : 0))
          app.excelItems.push({
            row: +index + +1,
            name: String(d.name),
            category: (d.category ? d.category.name : ''),
            phone: (d.phone ? String(d.phone) : ''),
            mobile: (d.mobile ? (d.mobile) : ''),
            email: (d.email ? d.email : ''),
            address: (d.address ? d.address : ''),
            remain: (d.remain ? d.remain.remain : ''),
          })
          return acc
        }, {
          amount: 0,
        })
        app.renderTable = true
        return totals
      },
    },
    watch: {
      taskDialogList (val, old) {
        if (!val) {
          this.getItems()
        }
      },
      taskDialog (val, old) {
        if (!val) {
          this.getItems()
        }
      },
      data (val, oldVal) {
        this.model = val.description
      },
      model () {
        this.$emit('input', this.model)
      },
      selected () {
        this.$emit('input', this.selected.text)
        if (this.selected.length > 0) {
          this.model = this.selected[0].text
          this.temp = this.selected[0].text
        }
        this.dialog = false
      },
      session (val) {
        if (val) {
          this.students = []
          this.getStudents()
        } else {
          this.students = []
        }
      },
    },
    activated () {
    },
    beforeMount () {
      if (this.sessionProp) {
        this.session = this.sessionProp
      }
    },
    mounted () {
      this.headersMap = this.headers
      this.selectedHeaders = this.headers
      if (this.input) {
        this.model = this.input
      }
      // this.getItems()
    },
    methods: {
      removeStudent (item) {
        const i = this.students.indexOf(item)
        if (i > -1) {
          this.students.splice(i, 1)
        }
        var app = this
        app.loading = true
        app.$axios.delete(this.$endpoint + 'academyStudents/' + item.registerId)
          .then(res => {
            res = res.data
          })
          .catch(err => {
            this.serverErrorHandeler(err)
          })
          .finally(() => (app.loading = false))
      },
      submit () {
        if (!this.students) {
          return false
        }
        var app = this
        app.loading = true
        app.clearing = true
        app.showing = true
        var data = {
          setAttendance: 'active',
          session: this.session,
          students: app.students,
        }
        app.$axios.post(this.$endpoint + 'academyStudents', data)
          .then(res => {
            res = res.data
            app.snakbarColor = 'green'
            app.snakbarText = this.$t('alert-created')
            app.snakbar = true
          })
          .catch(err => {
            app.serverErrorHandeler(err)
          })
          .finally(() => (app.loading = false))
      },
      getStudents () {
        var app = this
        app.loading = true
        app.$axios.get(this.$endpoint + 'academyStudents', {
          params: {
            getStudentsAttendance: 'active',
            session: app.session,
          },
        })
          .then(res => {
            res = res.data
            app.students = res
          })
          .catch(err => {
            app.serverErrorHandeler(err)
          })
          .finally(() => (app.loading = false))
      },
      openDeleteDialog (item) {
        this.deleteItem = item
        this.deletDialog = true
      },
      isEnabled (slot) {
        return this.enabled === slot
      },
    },
  }
</script>


<style scoped lang="scss">
$radius: 6px;
$border: rgba(15, 23, 42, .08);
$body-bg: #f3f7fc;

.sa-page {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 98px);
  background: $body-bg;
  border: 1px solid $border;
  border-radius: $radius;
  overflow: hidden;

  ::v-deep .v-input { height: auto !important; }
}

.sa-toolbar {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 16px;
  background: #fff;
  border-bottom: 1px solid $border;

  &__title {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
  }

  &__text {
    font-size: 1rem;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.sa-icon-tile {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: $radius;
  background: rgba(25, 118, 210, .1);
}

.sa-back {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: $radius;
  background: rgba(0, 0, 0, .05);
  transition: background .15s;

  &:hover { background: rgba(0, 0, 0, .09); }
}

.sa-body {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 16px;
  overflow: hidden;

  > * { flex-shrink: 0; }
}

.sa-card {
  display: flex;
  flex-direction: column;
  background: #fff;
  border: 1px solid $border;
  border-radius: $radius;
  box-shadow: 0 1px 3px rgba(15, 23, 42, .05);

  &__header {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    border-bottom: 1px solid $border;
  }

  &__title {
    font-size: .92rem;
    font-weight: 600;
  }

  &__subtitle {
    font-size: .76rem;
    opacity: .6;
  }

  &__body { padding: 14px 16px; }

  &--list {
    flex: 1 1 auto !important;
    flex-shrink: 1 !important;
    min-height: 220px;
    overflow: hidden;
  }
}

.sa-session-picker {
  max-width: 420px;
  margin-bottom: 12px;
}

.sa-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

  &__item {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 5px 10px;
    font-size: .8rem;
    border-radius: $radius;
    background: rgba(15, 23, 42, .04);

    b { font-weight: 600; }
  }
}

.sa-ltr { direction: ltr; unicode-bidi: embed; }

.sa-day {
  padding: 1px 6px;
  border-radius: $radius;
  font-size: .74rem;
  color: var(--v-primary-base);
  background: rgba(25, 118, 210, .1);
}

.sa-kpis {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}

.sa-kpi {
  padding: 10px 14px;
  background: #fff;
  border: 1px solid $border;
  border-inline-start: 4px solid var(--v-primary-base);
  border-radius: $radius;

  &__label { font-size: .76rem; opacity: .65; }
  &__value { font-size: 1.25rem; font-weight: 700; }

  &--present { border-inline-start-color: #2e7d32; }
  &--absent { border-inline-start-color: #c62828; }
}

.sa-list {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 6px 0;
}

.sa-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 36px 16px;
  font-size: .85rem;
  opacity: .6;
}

.sa-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 16px;
  border-bottom: 1px solid rgba(15, 23, 42, .05);
  transition: background .15s;

  &:last-child { border-bottom: 0; }
  &:hover { background: rgba(25, 118, 210, .04); }

  &__index {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    font-size: .78rem;
    font-weight: 600;
    background: rgba(198, 40, 40, .1);
    color: #c62828;
  }

  &__main { flex: 1 1 auto; min-width: 0; }

  &__name {
    font-size: .88rem;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__stats {
    display: flex;
    gap: 6px;
    margin-top: 3px;
  }

  &__state {
    flex-shrink: 0;
    font-size: .74rem;
    padding: 2px 8px;
    border-radius: $radius;
    color: #c62828;
    background: rgba(198, 40, 40, .08);
  }

  &__switch {
    flex-shrink: 0;
    margin: 0;
    padding: 0;
  }

  &--present {
    .sa-row__index { background: rgba(46, 125, 50, .12); color: #2e7d32; }
    .sa-row__state { background: rgba(46, 125, 50, .1); color: #2e7d32; }
  }
}

.sa-stat {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 0 6px;
  font-size: .72rem;
  border-radius: $radius;
  color: #01579b;
  background: rgba(1, 87, 155, .08);

  .v-icon { color: inherit; }

  &--present { color: #2e7d32; background: rgba(46, 125, 50, .1); }
  &--absent { color: #c62828; background: rgba(198, 40, 40, .08); }
}

.sa-footer {
  flex-shrink: 0;
  display: flex;
  justify-content: flex-start;
  padding: 10px 16px;
  background: #fff;
  border-top: 1px solid $border;

  &__btn {
    min-width: 120px;
    border-radius: $radius !important;
  }
}

/* mobile */
.sa-page--mobile {
  height: auto;
  min-height: calc(100vh - 98px);
  overflow: visible;

  .sa-body { padding: 12px; overflow: visible; }
  .sa-session-picker { max-width: none; }
  .sa-kpis { gap: 8px; }
  .sa-kpi { padding: 8px 10px; }
  .sa-kpi__value { font-size: 1.05rem; }
  .sa-card--list { min-height: 0; overflow: visible; }
  .sa-list { overflow: visible; }
  .sa-row { padding: 8px 12px; gap: 10px; }
  .sa-row__state { display: none; }

  .sa-footer {
    position: sticky;
    bottom: 0;
    z-index: 2;

    &__btn { flex: 1 1 auto; }
  }
}

/* dark */
.sa-page--dark {
  background: #121212;
  border-color: rgba(255, 255, 255, .08);

  .sa-toolbar,
  .sa-footer,
  .sa-card,
  .sa-kpi {
    background: #1e1e1e;
    border-color: rgba(255, 255, 255, .08);
  }

  .sa-card__header,
  .sa-row { border-color: rgba(255, 255, 255, .06); }

  .sa-kpi--total { border-inline-start-color: var(--v-primary-base); }
  .sa-kpi--present { border-inline-start-color: #66bb6a; }
  .sa-kpi--absent { border-inline-start-color: #ef5350; }

  .sa-back { background: rgba(255, 255, 255, .08); }
  .sa-back:hover { background: rgba(255, 255, 255, .14); }
  .sa-meta__item { background: rgba(255, 255, 255, .06); }
  .sa-icon-tile { background: rgba(100, 181, 246, .14); }
  .sa-row:hover { background: rgba(255, 255, 255, .04); }
  .sa-stat { color: #81d4fa; background: rgba(129, 212, 250, .12); }
}
</style>
